import { AxiosError } from "axios";

export interface ApiErrorResponse {
  error_type?: string;
  error_message?: string;
  message?: string;
  details?: string;
}

/**
 * Extracts a user-friendly error message from an AxiosError
 * @param error - The AxiosError object
 * @returns A user-friendly error message string
 */
export const getApiErrorMessage = (error: AxiosError<ApiErrorResponse>): string => {
  // Check if error.response.status exists safely using optional chaining
  const status = error?.response?.status;

  if (status) {
    switch (status) {
      case 400:
        return "Invalid request. Please check your input and try again.";
      case 401:
        return "Unauthorized. Please log in and try again.";
      case 402:
        return "Payment required. Please check your subscription or payment method.";
      case 403:
        return "Access restricted. Contact admin to perform this action.";
      case 404:
        return "Requested service not found.";
      case 408:
        return "Request timed out. Please check your connection and try again.";
      case 409:
        return "Request conflict. The data may have been modified by another user.";
      case 415:
        return "Unable to process the request. Please check your data and try again.";
      case 429:
        return "Too many requests. Please wait before trying again.";
      case 500:
        return "Server error. We're working to fix it. Please try again later.";
      case 502:
      case 503:
        return "Service temporarily unavailable. Please try again later.";
      case 504:
        return "Request timed out. Please try again later.";
      default:
        // Check for API error message safely
        const apiMessage = error?.response?.data?.message;
        if (apiMessage && typeof apiMessage === "string") {
          return apiMessage;
        }
        return `Unexpected error occurred (Code: ${status}). Please try again.`;
    }
  } else {
    // Frontend error or unknown error structure
    const message = error?.message;
    if (message) {
      if (message.includes("Network Error")) {
        return "Network error. Please check your internet connection.";
      }
      if (message.toLowerCase().includes("timeout")) {
        return "Request timed out. Please try again.";
      }
      return `An Error Occured : ${message}`;
    }
    return "An unexpected error occurred in the application.";
  }
};

/**
 * Checks if the error is a validation error that should be handled specially
 * @param error - The AxiosError object
 * @returns True if it's a validation error that needs special UI handling
 */
export const isValidationError = (error: AxiosError<ApiErrorResponse>): boolean => {
  return error.response?.data?.error_type === "Validation Error";
};
